import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import { Box, Grid, Typography } from '@mui/material';
import { Done, InfoOutlined } from '@mui/icons-material';
import { toast } from 'react-toastify';

import {
  FCheckbox,
  FHAntdDateTimePicker,
  FHButton,
  FHConfirmDialog,
  FHInputField,
  FHSelect,
  FHTooltip,
} from '../../../../base';
import COReadOnlyTextField from './COReadOnlyTextFields';
import { claimsActions } from '../../../../../store';
import {
  disabledFutureDate,
  disabledFutureHoursAndMinutes,
  getAllInvalidFieldNames,
  usDollarFormatter,
} from '../../../../../helpers';
import {
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_ORANGE,
  FORTA_GRAY4,
  FORTA_GRAY2,
  FORTA_GRAY3,
  FORTA_SUCCESS,
  FORTA_WHITE,
  FORTA_YELLOW,
  FORTA_YELLOW_2,
} from '../../../../../utils/colors';
import { getUtcOffset } from '../../../../../utils/common';
import { formatEntityId } from '../../../../../helpers/string-formatter';
import {
  ClaimRejectedStatus,
  ClaimRejectionReasonV2,
  ClaimStatus,
  RECOUPMENT_PROBABILITY_LABELS,
  RECOUPMENT_PROBABILITY_OPTIONS,
  RECOUPMENT_REASON_LABELS,
  RECOUPMENT_REASON_OPTIONS,
} from '../constants';
import CorrectClaimDialog from '../CorrectClaimConfirmDialog';
import {
  buildClaimUpdatePaylod,
  getSecondaryClaimStatusByPrimaryClaimStatus,
} from '../../claims/utils';
import { getClaimOverviewStatusSchema, getSecondaryClaimStatusSchema } from './validationSchema';
import {
  getClaimStatusOptions,
  isRecoupmentProbability,
  isRecoupmentReason,
  shouldShowPrimaryCorrectClaimButton,
  shouldShowSecondaryCorrectClaimButton,
} from './utils/general';
import { RECOUPMENT_SELECT_SX } from './styles';
import { ENTITY_PREFIX, PayorResponsibilityEnum, Themes } from '../../../../../constants';
import ErrorAlertIcon from '../../../../svgs/ErrorAlertIcon';

import type {
  ClaimRejectedStatus as ClaimRejectedStatusType,
  ClaimStatusType,
  RecoupmentProbabilityType,
  RecoupmentReasonType,
} from '../constants';
import type { RootState } from '../../../../../store';
import type { Moment } from 'moment';
import type { IClaim, SelectValueType } from '../../../../../utils/types';

function checkInvoiceableStatus(
  hasSecondaryPayor: boolean,
  claim?: IClaim | null,
): [boolean, string] {
  if (!claim) return [true, ''];
  if (claim?.balance && claim?.balance > 0) {
    return [true, 'Balance paid by the insurance must be zero to certify claim as invoiceable'];
  }
  // Primary claim status should be either Approved or Full Denial
  if (!(claim.status === ClaimStatus.APPROVED || claim.status === ClaimStatus.FULL_DENIAL)) {
    return [
      true,
      'The primary claim status must be either Approved or Full Denial in order to certify the claim as invoiceable.',
    ];
  }
  // Check for primary EOB document count
  const noPrimaryEOBDocumentCount =
    claim?.eob_documents?.filter(
      (doc) => doc.policy.payor_responsibility === PayorResponsibilityEnum.PRIMARY,
    ).length === 0;
  if (noPrimaryEOBDocumentCount) {
    return [
      true,
      'At least one EOB document for the primary policy should be uploaded to certify claim as invoiceable',
    ];
  }

  // If seconadry policy exists, check for EOB document and secondary claim status
  if (hasSecondaryPayor) {
    // Secondary claim status should be either Approved or Full Denial
    if (!(
      claim.secondary_status === ClaimStatus.APPROVED ||
      claim.secondary_status === ClaimStatus.FULL_DENIAL
    )) {
      return [
        true,
        'Secondary claim status must be either Approved or Full Denial in order to certify the claim as invoiceable.',
      ];
    }
    // Check for primary EOB document count
    const noSecondaryEOBDocumentCount =
      claim?.eob_documents?.filter(
        (doc) => doc.policy.payor_responsibility === PayorResponsibilityEnum.SECONDARY,
      ).length === 0;
    // Only check for EOB document when secondary claim is approved.
    if (claim.secondary_status === ClaimStatus.APPROVED && noSecondaryEOBDocumentCount) {
      return [
        true,
        'At least one EOB document for the secondary policy should be uploaded to certify claim as invoiceable',
      ];
    }
  }

  return [false, ''];
}

const claimOverviewStatusInitialState = {
  amount_recouped: null,
  date_amount_recouped: null,
  date_rejected: null,
  reason_for_rejection: null,
  status: null,
  date_primary_approved: null,
  recoupment_probability: null,
  recoupment_reason: null,
};

const secondaryClaimStatusInitialState = {
  secondary_status: null,
  secondary_reason_for_rejection: null,
  date_secondary_approved: null,
  date_secondary_payor_rejected: null,
  secondary_payor_claim_id: null,
  date_secondary_submitted_to_clearinghouse: null,
  utc_offset_date_secondary_submitted_to_clearinghouse: null,
};

interface IClaimOverviewStatusState {
  status: string | null;
  reason_for_rejection: string | null;
  date_rejected: string | null;
  amount_recouped: number | null;
  date_amount_recouped: string | null;
  date_primary_approved: string | null;
  recoupment_probability: RecoupmentProbabilityType | null;
  recoupment_reason: RecoupmentReasonType | null;
}

interface ISecondaryClaimStatusState {
  secondary_status: string | null;
  secondary_reason_for_rejection: string | null;
  date_secondary_payor_rejected: string | null;
  secondary_payor_claim_id: string | null;
  date_secondary_approved: string | null;
  date_secondary_submitted_to_clearinghouse: string | null;
  utc_offset_date_secondary_submitted_to_clearinghouse: number | null;
}

function isRejectedStatus(value: string | null): value is ClaimRejectedStatusType {
  return Object.values(ClaimRejectedStatus).some((status) => status === value);
}

interface IClaimOverviewStatusProps {
  isEditingInfo: boolean;
  allErrors: { [key: string]: boolean };
  onErrorUpdate: (err: { [key: string]: boolean }) => void;
}

function ClaimOverviewStatus({
  allErrors,
  isEditingInfo,
  onErrorUpdate,
}: IClaimOverviewStatusProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { current: claim, loading: claimsLoading } = useSelector(
    (state: RootState) => state.claims,
  );

  const disableClaimInfoUpdate = claimsLoading || claim?.status === ClaimStatus.UNBILLABLE;

  const [claimOverviewStatusState, setClaimOverviewStatusState] =
    useState<IClaimOverviewStatusState>(claimOverviewStatusInitialState);

  const [secondaryClaimStatusState, setSecondaryClaimStatusState] =
    useState<ISecondaryClaimStatusState>(secondaryClaimStatusInitialState);
  const [showCorrectClaimDialog, setShowCorrectClaimDialog] = useState<boolean>(false);
  const [finaliseClaimStatus, setFinaliseClaimStatus] = useState<
    ClaimStatusType | ClaimRejectedStatusType | null
  >(null);
  const [errors, setErrors] = useState<{ [key: string]: boolean }>();

  const hasSecondaryPayor =
    claim?.policies?.some(
      (policy) => policy.payor_responsibility === PayorResponsibilityEnum.SECONDARY,
    ) ?? false;

  const hasHistoricalRejectionReason = useMemo(
    () =>
      !Object.values(ClaimRejectionReasonV2).some(
        (val) => val === claimOverviewStatusState?.reason_for_rejection,
      ),
    [claimOverviewStatusState?.reason_for_rejection],
  );

  const [statusOptions, secClaimStatusOptions] = useMemo(
    () =>
      getClaimStatusOptions(
        claimOverviewStatusState?.status,
        secondaryClaimStatusState?.secondary_status,
      ),
    [claimOverviewStatusState?.status, secondaryClaimStatusState?.secondary_status],
  );

  const unbillableStatusOption = {
    value: ClaimStatus.UNBILLABLE,
    label: ClaimStatus.UNBILLABLE,
  };

  const [claimIsNotInvoiceable, tooltipContent] = checkInvoiceableStatus(hasSecondaryPayor, claim);

  const tooltipTitle =
    claimIsNotInvoiceable && isEditingInfo ? (
      <Box display="flex" alignItems="flex-start" justifyContent="flex-start" gap={1}>
        <InfoOutlined sx={{ color: FORTA_YELLOW, mt: 0.5 }} />
        <Typography fontWeight={400} textAlign="left">
          {tooltipContent}
        </Typography>
      </Box>
    ) : (
      ''
    );

  const clearingHouseDateSubmissionError =
    !claim?.date_submitted_to_clearinghouse && claim?.status !== ClaimStatus.UNBILLABLE;

  useEffect(() => {
    if (!claim) return;
    updateStateFromClaimData(claim);
    if (
      claim?.secondary_status &&
      claim?.secondary_status !== ClaimStatus.GENERATED &&
      claim?.secondary_status !== ClaimStatus.UNBILLABLE &&
      !claim.date_secondary_submitted_to_clearinghouse
    ) {
      setErrors({ date_secondary_submitted_to_clearinghouse: true });
      onErrorUpdate({ date_secondary_submitted_to_clearinghouse: true });
    } else {
      setErrors({});
      onErrorUpdate({ date_secondary_submitted_to_clearinghouse: false });
    }
  }, [claim]);

  useEffect(() => {
    if (!claim) return;
    setClaimOverviewStatusState({
      status: claim.status ?? null,
      reason_for_rejection: claim.reason_for_rejection ?? null,
      date_primary_approved: claim.date_primary_approved ?? null,
      date_rejected: claim.date_rejected ?? null,
      amount_recouped: claim.amount_recouped ?? null,
      date_amount_recouped: claim.date_amount_recouped ?? null,
      recoupment_probability: claim.recoupment_probability ?? null,
      recoupment_reason: claim.recoupment_reason ?? null,
    });
    setSecondaryClaimStatusState({
      secondary_reason_for_rejection: claim.secondary_reason_for_rejection ?? null,
      date_secondary_payor_rejected: claim.date_secondary_payor_rejected ?? null,
      secondary_status: claim.secondary_status ?? null,
      date_secondary_approved: claim.date_secondary_approved ?? null,
      secondary_payor_claim_id: claim.secondary_payor_claim_id ?? null,
      date_secondary_submitted_to_clearinghouse:
        claim.date_secondary_submitted_to_clearinghouse ?? null,
      utc_offset_date_secondary_submitted_to_clearinghouse:
        claim.utc_offset_date_secondary_submitted_to_clearinghouse ?? null,
    });
  }, [claim]);

  const updateStateFromClaimData = (claim: IClaim) => {
    setClaimOverviewStatusState({
      reason_for_rejection: claim.reason_for_rejection ?? null,
      date_rejected: claim.date_rejected ?? null,
      status: claim.status ?? null,
      date_primary_approved: claim.date_primary_approved ?? null,
      amount_recouped: claim.amount_recouped ?? null,
      date_amount_recouped: claim.date_amount_recouped ?? null,
      recoupment_probability: claim.recoupment_probability ?? null,
      recoupment_reason: claim.recoupment_reason ?? null,
    });
    setSecondaryClaimStatusState({
      secondary_reason_for_rejection: claim.secondary_reason_for_rejection ?? null,
      date_secondary_payor_rejected: claim.date_secondary_payor_rejected ?? null,
      secondary_status: claim.secondary_status ?? null,
      date_secondary_approved: claim.date_secondary_approved ?? null,
      secondary_payor_claim_id: claim.secondary_payor_claim_id ?? null,
      date_secondary_submitted_to_clearinghouse:
        claim.date_secondary_submitted_to_clearinghouse ?? null,
      utc_offset_date_secondary_submitted_to_clearinghouse:
        claim.utc_offset_date_secondary_submitted_to_clearinghouse ?? null,
    });
  };

  const handleSaveField = async (updates: any, showFinalizePopup = false) => {
    if (!claim) return;
    // Use the status being applied (falling back to the persisted one) so that
    // transitioning a claim TO Unbillable does not require a submit date - at this
    // point claim.status still holds the old billable status.
    const effectiveStatus = updates.status ?? claim.status;
    if (
      allErrors.date_submitted_to_clearinghouse &&
      effectiveStatus !== ClaimStatus.UNBILLABLE &&
      effectiveStatus !== ClaimStatus.GENERATED
    ) {
      toast.error('Please select Date submitted value.');
      if (claim) {
        updateStateFromClaimData(claim);
      }
      return;
    }
    setClaimOverviewStatusState({
      ...claimOverviewStatusState,
      ...updates,
    });
    const data = buildClaimUpdatePaylod(claim, updates);
    handleSave(data, showFinalizePopup);
  };

  const handleSaveSecondaryField = async (updates: any, showFinalizePopup = false) => {
    if (!claim) return;
    if (
      allErrors.date_secondary_submitted_to_clearinghouse &&
      claim.secondary_status &&
      claim.status !== ClaimStatus.UNBILLABLE &&
      claim.status !== ClaimStatus.GENERATED
    ) {
      toast.error('Please select Secondary claim date submitted value.');
      if (claim) {
        updateStateFromClaimData(claim);
      }
      return;
    }
    setSecondaryClaimStatusState({
      ...secondaryClaimStatusState,
      ...updates,
    });
    const data = buildClaimUpdatePaylod(claim, updates);
    if (
      showFinalizePopup &&
      data.status !== claim?.status && // Check if updating claim before checking Approved | Full denial
      (data.status === ClaimStatus.APPROVED || data.status === ClaimStatus.FULL_DENIAL)
    ) {
      setFinaliseClaimStatus(data.status);
      return;
    }

    const errObj: any = getAllInvalidFieldNames(
      getSecondaryClaimStatusSchema(data.secondary_status),
      {
        date_secondary_approved: data.date_secondary_approved,
        secondary_reason_for_rejection: data.secondary_reason_for_rejection,
        date_secondary_payor_rejected: data.date_secondary_payor_rejected,
        secondary_status: data.secondary_status,
      },
    );

    const err = {
      secondary_reason_for_rejection: errObj.secondary_reason_for_rejection ? true : false,
      date_secondary_payor_rejected: errObj.date_secondary_payor_rejected ? true : false,
      secondary_status: errObj.secondary_status ? true : false,
      date_secondary_approved: errObj.date_secondary_approved ? true : false,
    };
    setErrors(err);
    onErrorUpdate(err);
    setFinaliseClaimStatus(null);
    if (Object.values(err).some((item) => item)) {
      return;
    }
    handleSave(data);
  };

  const handleUpdateSecDateSubmitted = (
    dateKey: keyof ISecondaryClaimStatusState,
    newValue: Moment,
    save?: boolean,
  ) => {
    let val = newValue;
    if (moment(newValue, 'minute').isAfter(moment(new Date()), 'minute')) {
      val = moment(newValue).hours(0).minutes(0).seconds(0).milliseconds(0);
    }

    if (
      newValue &&
      moment(val).toISOString() !==
        moment(claim?.date_secondary_submitted_to_clearinghouse).toISOString()
    ) {
      if (
        claim?.date_finalized &&
        moment(moment(val).format('YYYY-MM-DD')).isAfter(claim.date_finalized)
      ) {
        toast.error('Date submitted should be less than date finalized');
        return;
      }
      const err = { ...errors, date_secondary_submitted_to_clearinghouse: false };
      setErrors(err);
      onErrorUpdate(err);
    }

    if (save) {
      if (
        moment(val).toISOString() ===
        moment(claim?.date_secondary_submitted_to_clearinghouse).toISOString()
      ) {
        return;
      }
      handleSaveField({
        [dateKey]: moment(val).toISOString(),
        utc_offset_date_secondary_submitted_to_clearinghouse: getUtcOffset(moment(new Date())),
      });
    } else {
      setSecondaryClaimStatusState({
        ...secondaryClaimStatusState,
        [dateKey]: moment(val).toISOString(),
        utc_offset_date_secondary_submitted_to_clearinghouse: getUtcOffset(moment(new Date())),
      });
    }
  };

  const handleSave = async (data: IClaim, showFinalizePopup = false) => {
    if (!claim) return;
    const errObj: any = getAllInvalidFieldNames(getClaimOverviewStatusSchema(data.status), {
      reason_for_rejection: data.reason_for_rejection,
      date_rejected: data.date_rejected,
      status: data.status,
      date_primary_approved: data.date_primary_approved,
    });

    const err = {
      reason_for_rejection: errObj.reason_for_rejection ? true : false,
      date_rejected: errObj.date_rejected ? true : false,
      status: errObj.status ? true : false,
      date_primary_approved: errObj.date_primary_approved ? true : false,
      // Since amount_recouped and date_amount_recouped are interdependent and Yup has
      // cyclic dependency issue, errors for these fields are calculated manually
      amount_recouped:
        data.date_amount_recouped && !data.amount_recouped && data.amount_recouped !== 0
          ? true
          : false,
      date_amount_recouped:
        data.amount_recouped != null && !data.date_amount_recouped ? true : false,
      recoupment_reason: Boolean(data.recoupment_probability) && !data.recoupment_reason,
    };
    setErrors(err);
    onErrorUpdate(err);
    setFinaliseClaimStatus(null);
    if (Object.values(err).some((item) => item)) {
      return;
    }
    if (
      showFinalizePopup &&
      data.status !== claim?.status && // Check if updating claim before checking Approved | Full denial
      (data.status === ClaimStatus.APPROVED || data.status === ClaimStatus.FULL_DENIAL) &&
      !hasSecondaryPayor
    ) {
      setFinaliseClaimStatus(data.status);
      return;
    }
    const response = await dispatch(
      claimsActions.updateClaim({
        getAccessTokenSilently,
        claimId: claim?.id,
        data,
      }),
    );
    setFinaliseClaimStatus(null);
    if (!response.payload) {
      setClaimOverviewStatusState({
        reason_for_rejection: claim.reason_for_rejection ?? null,
        date_rejected: claim.date_rejected ?? null,
        status: claim.status ?? null,
        date_primary_approved: claim.date_primary_approved ?? null,
        amount_recouped: claim.amount_recouped ?? null,
        date_amount_recouped: claim.date_amount_recouped ?? null,
        recoupment_probability: claim.recoupment_probability ?? null,
        recoupment_reason: claim.recoupment_reason ?? null,
      });
      setSecondaryClaimStatusState({
        secondary_reason_for_rejection: claim.secondary_reason_for_rejection ?? null,
        date_secondary_payor_rejected: claim.date_secondary_payor_rejected ?? null,
        secondary_status: claim.secondary_status ?? null,
        date_secondary_approved: claim.date_secondary_approved ?? null,
        secondary_payor_claim_id: claim.secondary_payor_claim_id ?? null,
        date_secondary_submitted_to_clearinghouse:
          claim.date_secondary_submitted_to_clearinghouse ?? null,
        utc_offset_date_secondary_submitted_to_clearinghouse:
          claim.utc_offset_date_secondary_submitted_to_clearinghouse ?? null,
      });
    }
  };

  const handleAmountUpdate = (key: keyof IClaimOverviewStatusState, value: string | null) => {
    if (!value) {
      setClaimOverviewStatusState({
        ...claimOverviewStatusState,
        [key]: null,
      });
      return;
    }
    // Allow only 2 decimal places
    if (!/^\d+(\.\d{0,2})?$/.test(value)) {
      return;
    }
    setClaimOverviewStatusState({
      ...claimOverviewStatusState,
      [key]: value ? value : null,
    });
  };

  const isRecoupmentReasonMissing =
    Boolean(claimOverviewStatusState.recoupment_probability) &&
    !claimOverviewStatusState.recoupment_reason;

  const handleRecoupmentProbabilityUpdate = (value: SelectValueType) => {
    if (!isRecoupmentProbability(value)) return;
    // The reason is required with a probability, so hold the probability locally until a reason is picked
    if (!claimOverviewStatusState.recoupment_reason) {
      setClaimOverviewStatusState({ ...claimOverviewStatusState, recoupment_probability: value });
      return;
    }
    handleSaveField({
      recoupment_probability: value,
      recoupment_reason: claimOverviewStatusState.recoupment_reason,
    });
  };

  const handleRecoupmentProbabilityClear = () => {
    // A held probability is not saved yet, so there is nothing to send
    if (!claim?.recoupment_probability) {
      setClaimOverviewStatusState({
        ...claimOverviewStatusState,
        recoupment_probability: null,
        recoupment_reason: null,
      });
      return;
    }
    handleSaveField({ recoupment_probability: null, recoupment_reason: null });
  };

  const handleRecoupmentReasonUpdate = (value: SelectValueType) => {
    if (!isRecoupmentReason(value)) return;
    handleSaveField({
      recoupment_probability: claimOverviewStatusState.recoupment_probability,
      recoupment_reason: value,
    });
  };

  const isRejected = isRejectedStatus(claimOverviewStatusState.status);

  const isSecondaryRejected = isRejectedStatus(secondaryClaimStatusState.secondary_status);

  const handleCertifyClaim = () => {
    if (!claim) return;
    dispatch(
      claimsActions.certifyClaim({
        getAccessTokenSilently,
        claimId: claim.id,
        data: {
          is_invoiceable: !claim.is_invoiceable,
          date_certified: moment(new Date()).toISOString(),
          utc_offset_date_certified: getUtcOffset(moment(new Date())),
        },
      }),
    );
  };

  return (
    <Box mb={5}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography textAlign="left" fontSize={21} color={FORTA_BLUE.primary} mb={2}>
          Status
        </Typography>
        {claim?.is_session_author_bcba && (
          <Typography
            sx={{
              backgroundColor: FORTA_YELLOW_2,
              border: `2px solid ${FORTA_YELLOW}`,
              height: '30px',
              fontWeight: 700,
              color: '#854d0e',
              px: 1,
              py: 0.3,
              lineHeight: '21px',
              borderRadius: '10px',
            }}
          >
            Contains BCBA sessions (97153)
          </Typography>
        )}
      </Box>

      {isRejected && (errors?.date_rejected || errors?.reason_for_rejection) && (
        <Box sx={{ display: 'flex', mb: 2 }}>
          <ErrorAlertIcon width={18} height={18} viewBox="0 0 26 24" color={FORTA_ERROR} />
          <Typography
            textAlign={'left'}
            alignSelf="start"
            lineHeight={'20px'}
            ml={0.5}
            mt={-0.1}
            color={FORTA_ERROR}
          >
            Rejection reason and date are required
          </Typography>
        </Box>
      )}
      {isEditingInfo && isRecoupmentReasonMissing && (
        <Box sx={{ display: 'flex', mb: 2 }}>
          <ErrorAlertIcon width={18} height={18} viewBox="0 0 26 24" color={FORTA_ERROR} />
          <Typography
            textAlign={'left'}
            alignSelf="start"
            lineHeight={'20px'}
            ml={0.5}
            mt={-0.1}
            color={FORTA_ERROR}
          >
            Recoupment reason is required
          </Typography>
        </Box>
      )}
      {claimOverviewStatusState?.status === ClaimStatus.GENERATED && (
        <Box sx={{ display: 'flex', mb: 2 }}>
          <ErrorAlertIcon width={18} height={18} viewBox="0 0 26 24" color={FORTA_BLUE.primary} />
          <Typography
            textAlign={'left'}
            alignSelf="start"
            lineHeight={'20px'}
            ml={0.5}
            mt={-0.1}
            color={FORTA_BLUE.primary}
          >
            Update Claim status to input data
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Grid
          container
          spacing={2}
          alignItems={'center'}
          justifyContent={
            claimOverviewStatusState.status === ClaimStatus.APPROVED
              ? 'flex-start'
              : 'space-between'
          }
        >
          <Grid item md={3} sm={6} xs={12}>
            <Typography fontWeight="bold" textAlign="left" mb={1}>
              Claim status
            </Typography>
            {isEditingInfo ? (
              <FHSelect
                data-testid="coStatus"
                fullWidth
                displayEmpty
                disabled={claimsLoading || Boolean(claim?.secondary_status)}
                sx={{
                  boxShadow: 'none',
                  borderRadius: '8px',
                  '& input::placeholder': { fontSize: '14px', color: FORTA_GRAY4 },
                  '&.Mui-disabled': {
                    WebkitTextFillColor: FORTA_GRAY3,
                    cursor: 'not-allowed',
                  },
                }}
                options={statusOptions}
                renderValue={
                  claimOverviewStatusState?.status
                    ? undefined
                    : () => (
                        <Typography color={FORTA_GRAY3} fontSize={14}>
                          Rejected
                        </Typography>
                      )
                }
                value={claimOverviewStatusState?.status || ''}
                onUpdate={(value: SelectValueType) => {
                  const updates = {
                    status: value.toString(),
                    date_primary_approved: null,
                    date_rejected: isRejectedStatus(value.toString())
                      ? claimOverviewStatusState.date_rejected
                      : null,
                    reason_for_rejection: isRejectedStatus(value.toString())
                      ? claimOverviewStatusState.reason_for_rejection
                      : null,
                  };
                  handleSaveField(updates, true);
                }}
                error={errors?.status}
              />
            ) : (
              <COReadOnlyTextField value={claimOverviewStatusState.status ?? null} />
            )}
          </Grid>
          {claimOverviewStatusState.status === ClaimStatus.APPROVED && (
            <>
              <Grid item md={3} sm={6} xs={12}>
                <Typography fontWeight="bold" textAlign="left" mb={1}>
                  Recoupment probability
                </Typography>
                {isEditingInfo ? (
                  <FHSelect
                    data-testid="coRecoupmentProbability"
                    fullWidth
                    displayEmpty
                    clearable={Boolean(claimOverviewStatusState.recoupment_probability)}
                    disabled={disableClaimInfoUpdate}
                    sx={RECOUPMENT_SELECT_SX}
                    options={RECOUPMENT_PROBABILITY_OPTIONS}
                    renderValue={
                      claimOverviewStatusState.recoupment_probability
                        ? undefined
                        : () => (
                            <Typography color={FORTA_GRAY3} fontSize={14}>
                              Select recoupment probability
                            </Typography>
                          )
                    }
                    value={claimOverviewStatusState.recoupment_probability ?? ''}
                    onUpdate={handleRecoupmentProbabilityUpdate}
                    onClear={handleRecoupmentProbabilityClear}
                  />
                ) : (
                  <COReadOnlyTextField
                    value={
                      claimOverviewStatusState.recoupment_probability
                        ? RECOUPMENT_PROBABILITY_LABELS[
                            claimOverviewStatusState.recoupment_probability
                          ]
                        : null
                    }
                  />
                )}
              </Grid>
              <Grid item md={3} sm={6} xs={12}>
                <Typography fontWeight="bold" textAlign="left" mb={1}>
                  Recoupment reason
                </Typography>
                {isEditingInfo ? (
                  <FHSelect
                    data-testid="coRecoupmentReason"
                    fullWidth
                    displayEmpty
                    disabled={
                      disableClaimInfoUpdate || !claimOverviewStatusState.recoupment_probability
                    }
                    sx={RECOUPMENT_SELECT_SX}
                    options={RECOUPMENT_REASON_OPTIONS}
                    renderValue={
                      claimOverviewStatusState.recoupment_reason
                        ? undefined
                        : () => (
                            <Typography color={FORTA_GRAY3} fontSize={14}>
                              Select recoupment reason
                            </Typography>
                          )
                    }
                    value={claimOverviewStatusState.recoupment_reason ?? ''}
                    onUpdate={handleRecoupmentReasonUpdate}
                    error={isRecoupmentReasonMissing}
                  />
                ) : (
                  <COReadOnlyTextField
                    value={
                      claimOverviewStatusState.recoupment_reason
                        ? RECOUPMENT_REASON_LABELS[claimOverviewStatusState.recoupment_reason]
                        : null
                    }
                  />
                )}
              </Grid>
            </>
          )}
          {claimOverviewStatusState.status === ClaimStatus.APPROVED && (
            <Grid item md={3} sm={6} xs={12}>
              <Typography fontWeight="bold" textAlign="left" mb={1}>
                Date approved
              </Typography>
              {isEditingInfo ? (
                <FHAntdDateTimePicker
                  data-testid="coDateApproved"
                  inputHeight={40}
                  sx={{
                    width: '100%',
                    boxShadow: 'none',
                    borderRadius: '8px',
                    '&.ant-picker-disabled': {
                      backgroundColor: FORTA_WHITE,
                      '& .ant-picker-input>input[disabled]': {
                        color: FORTA_GRAY3,
                      },
                    },
                  }}
                  format="MM/DD/YYYY"
                  allowClear={false}
                  disabled={disableClaimInfoUpdate}
                  disabledIconColor={FORTA_GRAY2}
                  disabledDate={disabledFutureDate}
                  value={
                    claimOverviewStatusState?.date_primary_approved
                      ? moment(claimOverviewStatusState?.date_primary_approved)
                      : ''
                  }
                  onSelect={(value: Moment) => {
                    const updates = {
                      date_primary_approved: moment(value).format('YYYY-MM-DD'),
                      date_rejected: null,
                      reason_for_rejection: null,
                      status: claimOverviewStatusState.status,
                      secondary_status:
                        hasSecondaryPayor && !secondaryClaimStatusState?.secondary_status
                          ? getSecondaryClaimStatusByPrimaryClaimStatus(
                              claimOverviewStatusState?.status ?? '',
                            )
                          : secondaryClaimStatusState?.secondary_status,
                    };
                    handleSaveField(updates, true);
                  }}
                  error={errors?.date_primary_approved}
                />
              ) : (
                <COReadOnlyTextField
                  value={
                    claimOverviewStatusState?.date_primary_approved
                      ? moment(claimOverviewStatusState?.date_primary_approved).format('MM/DD/YYYY')
                      : null
                  }
                />
              )}
            </Grid>
          )}
          {isRejected && (
            <>
              <Grid item md={3} sm={6} xs={12}>
                <Typography fontWeight="bold" textAlign="left" mb={1}>
                  Reason
                </Typography>
                {isEditingInfo ? (
                  <FHSelect
                    data-testid="coRejectionReason"
                    fullWidth
                    displayEmpty
                    disabled={claimsLoading}
                    sx={{
                      boxShadow: 'none',
                      borderRadius: '8px',
                      '& input::placeholder': { fontSize: '14px', color: FORTA_GRAY4 },
                      '&.Mui-disabled': {
                        WebkitTextFillColor: FORTA_GRAY3,
                        cursor: 'not-allowed',
                      },
                    }}
                    options={Object.values(ClaimRejectionReasonV2)
                      .map((reason) => ({
                        value: reason,
                        label: reason,
                      }))
                      .sort((a, b) => {
                        const aLabel = a.label.toLowerCase();
                        const bLabel = b.label.toLowerCase();
                        if (aLabel < bLabel) return -1;
                        if (aLabel > bLabel) return 1;
                        return 0;
                      })}
                    renderValue={
                      claimOverviewStatusState?.reason_for_rejection &&
                      !hasHistoricalRejectionReason
                        ? undefined
                        : () => (
                            <Typography color={FORTA_GRAY3} fontSize={14}>
                              Select
                            </Typography>
                          )
                    }
                    value={claimOverviewStatusState?.reason_for_rejection || ''}
                    onUpdate={(value: SelectValueType) => {
                      handleSaveField(
                        {
                          status: claimOverviewStatusState.status,
                          reason_for_rejection: value,
                          date_primary_approved: null,
                          date_rejected: claimOverviewStatusState.date_rejected,
                          secondary_status:
                            hasSecondaryPayor && !secondaryClaimStatusState?.secondary_status
                              ? getSecondaryClaimStatusByPrimaryClaimStatus(
                                  claimOverviewStatusState?.status ?? '',
                                )
                              : secondaryClaimStatusState?.secondary_status,
                        },
                        claimOverviewStatusState.date_rejected ? true : false,
                      );
                    }}
                    error={errors?.reason_for_rejection || hasHistoricalRejectionReason}
                  />
                ) : (
                  <COReadOnlyTextField
                    value={claimOverviewStatusState.reason_for_rejection ?? null}
                  />
                )}
              </Grid>
              <Grid item md={3} sm={6} xs={12}>
                <Typography fontWeight="bold" textAlign="left" mb={1}>
                  Rejection date
                </Typography>
                {isEditingInfo ? (
                  <FHAntdDateTimePicker
                    data-testid="coRejectionDate"
                    sx={{ width: '100%', mt: 3, boxShadow: 'none', borderRadius: '8px' }}
                    format="MM/DD/YYYY"
                    allowClear={false}
                    disabled={claimsLoading}
                    disabledDate={disabledFutureDate}
                    value={
                      claimOverviewStatusState?.date_rejected
                        ? moment(claimOverviewStatusState?.date_rejected)
                        : ''
                    }
                    onChange={(value: Moment) => {
                      handleSaveField(
                        {
                          date_rejected: value
                            ? moment(value).format('YYYY-MM-DD')
                            : claimOverviewStatusState.date_rejected,
                          status: claimOverviewStatusState.status,
                          date_primary_approved: null,
                          reason_for_rejection: claimOverviewStatusState.reason_for_rejection,
                          secondary_status:
                            hasSecondaryPayor && !secondaryClaimStatusState?.secondary_status
                              ? getSecondaryClaimStatusByPrimaryClaimStatus(
                                  claimOverviewStatusState?.status ?? '',
                                )
                              : secondaryClaimStatusState?.secondary_status,
                        },
                        claimOverviewStatusState.reason_for_rejection ? true : false,
                      );
                    }}
                    error={errors?.date_rejected}
                  />
                ) : (
                  <COReadOnlyTextField
                    value={
                      claimOverviewStatusState.date_rejected
                        ? moment(claimOverviewStatusState.date_rejected).format('MM/DD/YYYY')
                        : null
                    }
                  />
                )}
              </Grid>
            </>
          )}
          {shouldShowPrimaryCorrectClaimButton(claim) && (
            <Grid item md={3} sm={6} xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <FHButton
                data-testid="coCorrectClaimButton"
                sx={{ width: '165px', mt: 4 }}
                disabled={claimsLoading}
                onClick={() => setShowCorrectClaimDialog(true)}
              >
                Correct claim
              </FHButton>
            </Grid>
          )}
          {/* //always align this grid at the end of the column */}
          <Grid item md={3} sm={6} xs={12} sx={{ display: 'flex-column' }}>
            <Box display="flex" alignItems="center" justifyContent="flex-start" gap={1} mb={1}>
              <Typography fontWeight="bold" textAlign="left">
                Invoice
              </Typography>
              {claim?.invoice_id && (
                <Link
                  style={{
                    fontSize: '15px',
                    color: FORTA_BLUE.primary,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'inline-block',
                  }}
                  to={`/billings/invoice/${claim.invoice_id}`}
                >
                  {' '}
                  - {formatEntityId(claim.invoice_id, ENTITY_PREFIX.Invoice)}
                </Link>
              )}
            </Box>

            {isEditingInfo ? (
              <FHTooltip
                arrow
                title={tooltipTitle}
                tooltipBorder={`1px solid ${FORTA_YELLOW}`}
                placement="left-start"
                sx={{
                  borderRadius: '10px',
                  display: 'flex',
                  maxWidth: '350px',
                }}
                tooltipbackground={FORTA_WHITE}
                tooltipBoxShadow={'0px 4px 4px rgba(0, 0, 0, 0.25)'}
              >
                <Box display="flex" alignItems="center" justifyContent="flex-start">
                  <FCheckbox
                    colorTheme={Themes.Primary}
                    sx={{
                      marginRight: '4px',
                      boxShadow: 'none',
                      '&.Mui-disabled': {
                        WebkitTextFillColor: FORTA_GRAY3,
                        cursor: 'not-allowed',
                      },
                    }}
                    disabled={disableClaimInfoUpdate || claimIsNotInvoiceable}
                    checked={claim?.is_invoiceable}
                    onClick={handleCertifyClaim}
                    data-testid="readyForInvoiceCheckbox"
                  />
                  <Typography fontWeight={400} textAlign="left">
                    Claim is ready to be invoiced
                  </Typography>
                </Box>
              </FHTooltip>
            ) : (
              <Box display="flex" alignItems="center" justifyContent="flex-start" gap={1}>
                {!claim?.invoice_id ? (
                  claim?.is_invoiceable ? (
                    <>
                      <InfoOutlined sx={{ color: FORTA_BLUE.bright }} />
                      <Typography fontWeight={400} textAlign="left">
                        Claim is ready to be invoiced
                      </Typography>
                    </>
                  ) : (
                    <>
                      <InfoOutlined sx={{ color: FORTA_ORANGE }} />
                      <Typography fontWeight={400} textAlign="left">
                        Claim is not ready to be invoiced
                      </Typography>
                    </>
                  )
                ) : (
                  <Box display="flex-column" alignItems="center" justifyContent="flex-start">
                    <Box display="flex" alignItems="center" justifyContent="flex-start" gap={1}>
                      <Done sx={{ color: FORTA_SUCCESS }} />
                      <Typography fontWeight={400} textAlign="left">
                        Claim is already invoiced
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
      {(claim?.status === ClaimStatus.APPROVED ||
        claim?.status === ClaimStatus.FULL_DENIAL ||
        claim?.status === ClaimStatus.UNBILLABLE) &&
        hasSecondaryPayor && (
          <>
            <Typography textAlign="left" fontSize={21} color={FORTA_BLUE.primary} mb={2} mt={4}>
              Secondary claim details
            </Typography>
            <Box sx={{ height: '1px', mt: 2, backgroundColor: FORTA_BLUE.light2 }}></Box>
            <Box sx={{ mt: 2 }}>
              {clearingHouseDateSubmissionError && isEditingInfo && (
                <Box sx={{ display: 'flex' }} my={2}>
                  <ErrorAlertIcon width={18} height={18} viewBox="0 0 26 24" color={FORTA_ERROR} />
                  <Typography
                    textAlign={'left'}
                    alignSelf="start"
                    lineHeight={'18px'}
                    ml={0.5}
                    color={FORTA_ERROR}
                  >
                    Please provide the primary claim date submitted value before updating the
                    secondary claim values.
                  </Typography>
                </Box>
              )}
              <Grid container spacing={2} alignItems={'center'}>
                <Grid item md={3} sm={6} xs={12}>
                  <Typography fontWeight="bold" textAlign="left" mb={1}>
                    Secondary claim status
                  </Typography>
                  {isEditingInfo ? (
                    <FHSelect
                      data-testid="secondaryStatus"
                      fullWidth
                      displayEmpty
                      disabled={claimsLoading || clearingHouseDateSubmissionError}
                      sx={{
                        '& input::placeholder': { fontSize: '14px', color: FORTA_GRAY4 },
                        '&.Mui-disabled': {
                          WebkitTextFillColor: FORTA_GRAY3,
                          cursor: 'not-allowed',
                        },
                      }}
                      options={
                        claimOverviewStatusState?.status === ClaimStatus.UNBILLABLE
                          ? [unbillableStatusOption]
                          : secClaimStatusOptions
                      }
                      renderValue={
                        secondaryClaimStatusState?.secondary_status
                          ? undefined
                          : () => (
                              <Typography color={FORTA_GRAY3} fontSize={14}>
                                Select secondary status
                              </Typography>
                            )
                      }
                      value={secondaryClaimStatusState?.secondary_status || ''}
                      onUpdate={(value: SelectValueType) => {
                        const updates = {
                          secondary_status: value.toString(),
                          date_secondary_approved: null,
                          date_secondary_payor_rejected: isRejectedStatus(value.toString())
                            ? secondaryClaimStatusState.date_secondary_payor_rejected
                            : null,
                          secondary_reason_for_rejection: isRejectedStatus(value.toString())
                            ? secondaryClaimStatusState.secondary_reason_for_rejection
                            : null,
                        };
                        handleSaveSecondaryField(updates, true);
                      }}
                      error={errors?.secondary_status}
                    />
                  ) : (
                    <COReadOnlyTextField
                      value={secondaryClaimStatusState.secondary_status ?? null}
                    />
                  )}
                </Grid>
                {secondaryClaimStatusState.secondary_status === ClaimStatus.APPROVED && (
                  <Grid item sm={6} xs={12} flexGrow={1}>
                    <Typography fontWeight="bold" textAlign="left" mb={1}>
                      Date secondary approved
                    </Typography>
                    {isEditingInfo ? (
                      <FHAntdDateTimePicker
                        data-testid="coDateSeconadryApproved"
                        inputHeight={40}
                        sx={{
                          width: '43%',
                          boxShadow: 'none',
                          borderRadius: '8px',
                          '&.ant-picker-disabled': {
                            backgroundColor: FORTA_WHITE,
                            '& .ant-picker-input>input[disabled]': {
                              color: FORTA_GRAY3,
                            },
                          },
                        }}
                        format="MM/DD/YYYY"
                        allowClear={false}
                        disabled={disableClaimInfoUpdate}
                        disabledIconColor={FORTA_GRAY2}
                        disabledDate={disabledFutureDate}
                        value={
                          secondaryClaimStatusState?.date_secondary_approved
                            ? moment(secondaryClaimStatusState?.date_secondary_approved)
                            : ''
                        }
                        onSelect={(value: Moment) => {
                          const updates = {
                            date_secondary_approved: moment(value).format('YYYY-MM-DD'),
                            date_secondary_payor_rejected: null,
                            secondary_reason_for_rejection: null,
                            secondary_status: secondaryClaimStatusState.secondary_status,
                          };
                          handleSaveField(updates, true);
                        }}
                        error={errors?.date_secondary_approved}
                      />
                    ) : (
                      <COReadOnlyTextField
                        value={
                          secondaryClaimStatusState?.date_secondary_approved
                            ? moment(secondaryClaimStatusState?.date_secondary_approved).format(
                                'MM/DD/YYYY',
                              )
                            : null
                        }
                      />
                    )}
                  </Grid>
                )}
                {isSecondaryRejected && (
                  <>
                    <Grid item md={3} sm={6} xs={12}>
                      <Typography fontWeight="bold" textAlign="left" mb={1}>
                        Reason
                      </Typography>
                      {isEditingInfo ? (
                        <FHSelect
                          data-testid="secondaryRejectionReason"
                          fullWidth
                          displayEmpty
                          disabled={claimsLoading || clearingHouseDateSubmissionError}
                          sx={{
                            '& input::placeholder': { fontSize: '14px', color: FORTA_GRAY4 },
                            '&.Mui-disabled': {
                              WebkitTextFillColor: FORTA_GRAY3,
                              cursor: 'not-allowed',
                            },
                          }}
                          options={Object.values(ClaimRejectionReasonV2)
                            .map((reason) => ({
                              value: reason,
                              label: reason,
                            }))
                            .sort((a, b) => {
                              const aLabel = a.label.toLowerCase();
                              const bLabel = b.label.toLowerCase();
                              if (aLabel < bLabel) return -1;
                              if (aLabel > bLabel) return 1;
                              return 0;
                            })}
                          renderValue={
                            secondaryClaimStatusState?.secondary_reason_for_rejection
                              ? undefined
                              : () => (
                                  <Typography color={FORTA_GRAY3} fontSize={14}>
                                    Select
                                  </Typography>
                                )
                          }
                          value={secondaryClaimStatusState?.secondary_reason_for_rejection || ''}
                          onUpdate={(value: SelectValueType) => {
                            handleSaveSecondaryField({
                              secondary_status: secondaryClaimStatusState.secondary_status,
                              secondary_reason_for_rejection: value,
                              date_secondary_approved: null,
                              date_secondary_payor_rejected:
                                secondaryClaimStatusState.date_secondary_payor_rejected,
                            });
                          }}
                          error={errors?.secondary_reason_for_rejection}
                        />
                      ) : (
                        <COReadOnlyTextField
                          value={secondaryClaimStatusState?.secondary_reason_for_rejection ?? null}
                        />
                      )}
                    </Grid>
                    <Grid item md={3} sm={6} xs={12}>
                      <Typography fontWeight="bold" textAlign="left" mb={1}>
                        Rejection date
                      </Typography>
                      {isEditingInfo ? (
                        <FHAntdDateTimePicker
                          data-testid="secondaryRejectionDate"
                          sx={{ width: '100%', mt: 3, boxShadow: 'none', borderRadius: '8px' }}
                          format="MM/DD/YYYY"
                          allowClear={false}
                          disabled={claimsLoading || clearingHouseDateSubmissionError}
                          disabledDate={disabledFutureDate}
                          value={
                            secondaryClaimStatusState?.date_secondary_payor_rejected
                              ? moment(secondaryClaimStatusState?.date_secondary_payor_rejected)
                              : ''
                          }
                          onChange={(value: Moment) => {
                            handleSaveSecondaryField({
                              date_secondary_payor_rejected: value
                                ? moment(value).format('YYYY-MM-DD')
                                : secondaryClaimStatusState?.date_secondary_payor_rejected,
                              secondary_status: secondaryClaimStatusState.secondary_status,
                              date_secondary_approved: null,
                              secondary_reason_for_rejection:
                                secondaryClaimStatusState.secondary_reason_for_rejection,
                            });
                          }}
                          onSelect={(value: Moment) => {
                            handleSaveSecondaryField({
                              date_secondary_payor_rejected: value
                                ? moment(value).format('YYYY-MM-DD')
                                : secondaryClaimStatusState.date_secondary_payor_rejected,
                              secondary_status: secondaryClaimStatusState.secondary_status,
                              date_secondary_approved: null,
                              secondary_reason_for_rejection:
                                secondaryClaimStatusState.secondary_reason_for_rejection,
                            });
                          }}
                          error={errors?.date_secondary_payor_rejected}
                        />
                      ) : (
                        <COReadOnlyTextField
                          value={
                            secondaryClaimStatusState.date_secondary_payor_rejected
                              ? moment(
                                  secondaryClaimStatusState.date_secondary_payor_rejected,
                                ).format('MM/DD/YYYY')
                              : null
                          }
                        />
                      )}
                    </Grid>
                    {shouldShowSecondaryCorrectClaimButton(claim) && (
                      <Grid
                        item
                        md={3}
                        sm={6}
                        xs={12}
                        sx={{ display: 'flex', justifyContent: 'flex-end' }}
                      >
                        <FHButton
                          data-testid="correctSecondaryClaimBtn"
                          sx={{ width: '165px', mt: 4 }}
                          disabled={claimsLoading}
                          onClick={() => {
                            if (!claim?.date_secondary_submitted_to_clearinghouse) {
                              toast.error('Please select Secondary claim date submitted value.');
                              return;
                            }
                            setShowCorrectClaimDialog(true);
                          }}
                        >
                          Correct claim
                        </FHButton>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
              <Grid container spacing={2} mt={1} alignItems={'center'}>
                <Grid item md={3} sm={6} xs={12}>
                  <Typography fontWeight="bold" textAlign="left" mb={1}>
                    Secondary payor claim ID
                  </Typography>
                  {isEditingInfo ? (
                    <FHInputField
                      fullWidth
                      inputProps={{
                        'data-testid': 'secondaryPayorClaimId',
                      }}
                      disabled={disableClaimInfoUpdate || clearingHouseDateSubmissionError}
                      placeholder="Enter ID"
                      sx={{
                        '& input::placeholder': { fontSize: '14px', color: FORTA_GRAY4 },
                        '&.Mui-disabled input': {
                          WebkitTextFillColor: `${FORTA_GRAY3} !important`,
                          cursor: 'not-allowed',
                        },
                      }}
                      value={secondaryClaimStatusState.secondary_payor_claim_id ?? ''}
                      onChange={(e) => {
                        setSecondaryClaimStatusState({
                          ...secondaryClaimStatusState,
                          secondary_payor_claim_id: e.target.value,
                        });
                      }}
                      onBlur={() =>
                        handleSaveSecondaryField({
                          secondary_payor_claim_id:
                            secondaryClaimStatusState.secondary_payor_claim_id,
                        })
                      }
                    />
                  ) : (
                    <COReadOnlyTextField
                      value={secondaryClaimStatusState.secondary_payor_claim_id ?? null}
                    />
                  )}
                </Grid>
                <Grid item md={3} sm={6} xs={12}>
                  <Typography fontWeight="bold" textAlign="left" mb={1}>
                    Secondary claim date submitted
                  </Typography>
                  {isEditingInfo ? (
                    <FHAntdDateTimePicker
                      data-testid="dateSubmittedSecondary"
                      sx={{
                        width: '100%',
                        '&.ant-picker-disabled': {
                          backgroundColor: FORTA_WHITE,
                          '& .ant-picker-input>input[disabled]': {
                            color: FORTA_GRAY3,
                          },
                        },
                      }}
                      format="MM/DD/YYYY h:mm a"
                      showTime={{ format: 'h:mm a' }}
                      allowClear={false}
                      showNow={true}
                      use12Hours
                      disabled={claimsLoading || clearingHouseDateSubmissionError}
                      disabledIconColor={FORTA_GRAY2}
                      disabledDate={disabledFutureDate}
                      disabledTime={(current: Moment) => disabledFutureHoursAndMinutes(current)}
                      error={errors?.['date_secondary_submitted_to_clearinghouse']}
                      value={
                        secondaryClaimStatusState?.date_secondary_submitted_to_clearinghouse
                          ? moment(
                              secondaryClaimStatusState?.date_secondary_submitted_to_clearinghouse,
                            )
                          : ''
                      }
                      onChange={(value: Moment) => {
                        handleUpdateSecDateSubmitted(
                          'date_secondary_submitted_to_clearinghouse',
                          value?.seconds(0).milliseconds(0),
                        );
                      }}
                      onSelect={(value: Moment) => {
                        handleUpdateSecDateSubmitted(
                          'date_secondary_submitted_to_clearinghouse',
                          value?.seconds(0).milliseconds(0),
                        );
                      }}
                      onBlur={() => {
                        handleUpdateSecDateSubmitted(
                          'date_secondary_submitted_to_clearinghouse',
                          moment(
                            secondaryClaimStatusState?.date_secondary_submitted_to_clearinghouse,
                          ),
                          true,
                        );
                      }}
                      onOk={(value: Moment) => {
                        handleUpdateSecDateSubmitted(
                          'date_secondary_submitted_to_clearinghouse',
                          value?.seconds(0).milliseconds(0),
                          true,
                        );
                      }}
                    />
                  ) : (
                    <COReadOnlyTextField
                      value={
                        secondaryClaimStatusState?.date_secondary_submitted_to_clearinghouse
                          ? moment(
                              secondaryClaimStatusState?.date_secondary_submitted_to_clearinghouse,
                            ).format('MM/DD/YYYY - hh:mm a')
                          : null
                      }
                    />
                  )}
                </Grid>
              </Grid>
            </Box>
            <Box sx={{ height: '1px', mt: 2.7, mb: 4, backgroundColor: FORTA_BLUE.light2 }}></Box>
          </>
        )}
      <Box mt={2}>
        <Grid container spacing={2} alignItems={'center'}>
          <Grid item md={3} sm={6} xs={12}>
            <Typography fontWeight="bold" textAlign="left" mb={1}>
              Amount recouped
            </Typography>
            {isEditingInfo ? (
              <FHInputField
                inputProps={{
                  'data-testid': 'coAmountRecouped',
                }}
                fullWidth
                disabled={disableClaimInfoUpdate}
                placeholder="Enter amount"
                sx={{
                  boxShadow: 'none',
                  borderRadius: '8px',
                  '& input::placeholder': { fontSize: '14px', color: FORTA_GRAY4 },
                  '&.Mui-disabled input': {
                    WebkitTextFillColor: `${FORTA_GRAY3} !important`,
                    cursor: 'not-allowed',
                  },
                }}
                value={claimOverviewStatusState.amount_recouped ?? ''}
                onChange={(e) => {
                  const trimmed = e.target.value.trim();
                  handleAmountUpdate('amount_recouped', trimmed === '' ? null : trimmed);
                }}
                onBlur={(e) => {
                  const trimmed = e.target.value.trim();
                  handleSaveField({
                    amount_recouped: trimmed === '' ? null : parseFloat(trimmed),
                    date_amount_recouped: claimOverviewStatusState.date_amount_recouped,
                  });
                }}
                error={errors?.amount_recouped}
              />
            ) : (
              <COReadOnlyTextField
                value={
                  claimOverviewStatusState.amount_recouped === null
                    ? null
                    : usDollarFormatter.format(claimOverviewStatusState.amount_recouped)
                }
              />
            )}
          </Grid>
          <Grid item md={3} sm={6} xs={12}>
            <Typography fontWeight="bold" textAlign="left" mb={1}>
              Date recouped
            </Typography>
            {isEditingInfo ? (
              <FHAntdDateTimePicker
                data-testid="coDateAmountRecouped"
                sx={{
                  width: '100%',
                  boxShadow: 'none',
                  borderRadius: '8px',
                  '&.ant-picker-disabled': {
                    backgroundColor: FORTA_WHITE,
                    '& .ant-picker-input>input[disabled]': {
                      color: FORTA_GRAY3,
                    },
                  },
                }}
                format="MM/DD/YYYY"
                allowClear={false}
                disabled={disableClaimInfoUpdate}
                disabledIconColor={FORTA_GRAY2}
                disabledDate={disabledFutureDate}
                value={
                  claimOverviewStatusState.date_amount_recouped
                    ? moment(claimOverviewStatusState.date_amount_recouped)
                    : ''
                }
                onSelect={(value: Moment) => {
                  handleSaveField({
                    date_amount_recouped: value
                      ? moment(value).format('YYYY-MM-DD')
                      : claimOverviewStatusState.date_amount_recouped,
                    amount_recouped: claimOverviewStatusState.amount_recouped,
                  });
                }}
                error={errors?.date_amount_recouped}
              />
            ) : (
              <COReadOnlyTextField
                value={
                  claimOverviewStatusState.date_amount_recouped
                    ? moment(claimOverviewStatusState.date_amount_recouped).format('MM/DD/YYYY')
                    : null
                }
              />
            )}
          </Grid>
        </Grid>
      </Box>
      <CorrectClaimDialog
        show={showCorrectClaimDialog}
        onClose={() => setShowCorrectClaimDialog(false)}
      />
      <FHConfirmDialog
        data-testid="finaliseClaimDialog"
        dialogStyles={{
          padding: '24px',
          height: 'auto',
          width: '550px',
          borderRadius: '16px',
        }}
        sx={{
          '& .MuiModal-backdrop': {
            backdropFilter: 'blur(2px)',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
          },
        }}
        open={Boolean(finaliseClaimStatus)}
        colorTheme={Themes.Primary}
        title={'Finalize claim'}
        contentRenderer={() => (
          <Box px={2} py={4}>
            <Typography display="inline">Changing the status to </Typography>
            <Typography
              display="inline"
              fontWeight={700}
              color={
                claimOverviewStatusState.status === ClaimStatus.APPROVED
                  ? FORTA_SUCCESS
                  : FORTA_ERROR
              }
            >
              {`${
                claimOverviewStatusState.status === ClaimStatus.APPROVED
                  ? 'APPROVED'
                  : 'FULL DENIAL'
              } `}
            </Typography>
            <Typography display="inline">
              will finalize the claim and prohibit any further changes. Do you wish to continue?
            </Typography>
          </Box>
        )}
        submittext={'Continue'}
        canceltext={'Cancel'}
        onClose={() => {
          setFinaliseClaimStatus(null);
          // Fall back to previous state
          setClaimOverviewStatusState({
            reason_for_rejection: claim?.reason_for_rejection ?? null,
            date_rejected: claim?.date_rejected ?? null,
            status: claim?.status ?? null,
            date_primary_approved: claim?.date_primary_approved ?? null,
            amount_recouped: claim?.amount_recouped ?? null,
            date_amount_recouped: claim?.date_amount_recouped ?? null,
            recoupment_probability: claim?.recoupment_probability ?? null,
            recoupment_reason: claim?.recoupment_reason ?? null,
          });
        }}
        onSubmit={() => {
          if (!claim) return;
          handleSave(
            buildClaimUpdatePaylod(claim, {
              status: claimOverviewStatusState.status,
              date_primary_approved: claimOverviewStatusState.date_primary_approved,
              reason_for_rejection:
                finaliseClaimStatus === ClaimStatus.FULL_DENIAL
                  ? claimOverviewStatusState.reason_for_rejection
                  : null, // reason_for_rejection and date_rejected are only allowed in case status is rejected
              date_rejected:
                finaliseClaimStatus === ClaimStatus.FULL_DENIAL
                  ? claimOverviewStatusState.date_rejected
                  : null,
            }),
          );
        }}
      />
    </Box>
  );
}

export default ClaimOverviewStatus;
